import { Check } from "lucide-react";
import { useTranslations } from "use-intl";
import { HighlightedCodeEditor } from "./highlighted-code-editor";
import {
  configEditorLanguage,
  isCodeEditorKey,
  isJsonEditorKey,
  isJsonLike,
} from "./structured-config-utils";
import { defaultCatalogValueForKey, type CatalogFieldDef } from "./catalog-step-config-utils";
import {
  CatalogStepDomainField,
  catalogStepDomainFieldKind,
} from "./catalog-step-domain-widgets";
import { FieldShell } from "./catalog-step-domain-primitives";
import { catalogFieldLabelKey } from "./catalog-field-labels";
import {
  KeyValueObjectField,
  MultiEnumField,
  TextConfigField,
} from "./catalog-step-primitive-widgets";
import type { StepType } from "@/types/workflow.types";

interface CatalogFieldProps {
  field: CatalogFieldDef;
  nodeType: StepType;
  value: unknown;
  draft: string;
  error?: string;
  setValue: (key: string, value: unknown) => void;
  setDraft: (key: string, value: string) => void;
}

export function CatalogStepConfigField({
  field,
  nodeType,
  value,
  draft,
  error,
  setValue,
  setDraft,
}: CatalogFieldProps) {
  const t = useTranslations("workflows");
  const labelKey = catalogFieldLabelKey(field.key);
  const displayField = labelKey ? { ...field, label: t(`drawer.field.${labelKey}`) } : field;
  const domainKind = catalogStepDomainFieldKind(displayField, nodeType);
  if (domainKind) {
    return (
      <CatalogStepDomainField
        kind={domainKind}
        field={displayField}
        nodeType={nodeType}
        value={value}
        draft={draft}
        error={error}
        setValue={setValue}
        setDraft={setDraft}
      />
    );
  }
  if (displayField.schema.type === "boolean") {
    return <BooleanField field={displayField} value={value} setValue={setValue} />;
  }
  if (displayField.schema.type === "array" && displayField.schema.items?.enum?.length) {
    return <MultiEnumField field={displayField} value={value} setValue={setValue} />;
  }
  if (displayField.schema.enum?.length) {
    return <EnumField field={displayField} value={value} setValue={setValue} />;
  }
  if (displayField.schema.type === "number" || displayField.schema.type === "integer") {
    return <NumberField field={displayField} value={value} setValue={setValue} />;
  }
  if (
    displayField.schema.type === "object" &&
    displayField.schema.additionalProperties &&
    !displayField.schema.properties
  ) {
    return <KeyValueObjectField field={displayField} value={value} setValue={setValue} />;
  }
  if (
    displayField.schema.type === "object" ||
    displayField.schema.type === "array" ||
    isJsonLike(value) ||
    isJsonEditorKey(displayField.key.toLowerCase()) ||
    isCodeEditorKey(displayField.key.toLowerCase())
  ) {
    return (
      <StructuredField
        field={displayField}
        value={value}
        draft={draft}
        error={error}
        setDraft={setDraft}
      />
    );
  }
  return <TextConfigField field={displayField} value={value} setValue={setValue} />;
}

function BooleanField({
  field,
  value,
  setValue,
}: Pick<CatalogFieldProps, "field" | "value" | "setValue">) {
  const checked = Boolean(value);
  return (
    <button
      type="button"
      aria-pressed={checked}
      onClick={() => setValue(field.key, !checked)}
      className="flex items-center gap-2 rounded-lg border border-[var(--border)] px-3 py-2 text-left text-sm transition-colors hover:bg-[var(--muted)] focus:outline-none focus:shadow-[var(--shadow-focus-ring)]"
    >
      <span
        aria-hidden
        className={`flex size-4 shrink-0 items-center justify-center rounded-[4px] border-[1.5px] ${
          checked
            ? "border-[var(--primary)] bg-[var(--primary)] text-[var(--primary-foreground)]"
            : "border-[var(--border-strong)]"
        }`}
      >
        {checked && <Check className="size-3" strokeWidth={3} />}
      </span>
      <span className="font-medium text-[var(--foreground)]">{field.label}</span>
    </button>
  );
}

function EnumField({
  field,
  value,
  setValue,
}: Pick<CatalogFieldProps, "field" | "value" | "setValue">) {
  return (
    <FieldShell label={field.label}>
      <select
        aria-label={field.label}
        value={typeof value === "string" ? value : ""}
        onChange={(event) => setValue(field.key, event.target.value)}
        className="h-9 w-full rounded-lg border border-[var(--border)] bg-[var(--card)] px-3 text-sm focus:border-[var(--ring)] focus:outline-none"
      >
        <option value="">Not set</option>
        {field.schema.enum?.map((option) => (
          <option key={option} value={option}>
            {option}
          </option>
        ))}
      </select>
    </FieldShell>
  );
}

function NumberField({
  field,
  value,
  setValue,
}: Pick<CatalogFieldProps, "field" | "value" | "setValue">) {
  const t = useTranslations("workflows");
  const defaultValue = defaultCatalogValueForKey(field.key, field.schema);
  const zeroUnlimited = zeroMeansNoLimit(field.key);
  const hint =
    typeof defaultValue === "number"
      ? zeroUnlimited
        ? t("drawer.field_hint.default_zero_unlimited", { value: defaultValue })
        : t("drawer.field_hint.default_value", { value: defaultValue })
      : zeroUnlimited
        ? t("drawer.field_hint.zero_unlimited")
        : field.schema.description;
  return (
    <FieldShell label={field.label} hint={hint}>
      <input
        aria-label={field.label}
        type="number"
        step={field.schema.type === "integer" ? 1 : "any"}
        value={typeof value === "number" && Number.isFinite(value) ? value : ""}
        onChange={(event) =>
          setValue(field.key, event.target.value === "" ? undefined : Number(event.target.value))
        }
        className="h-9 w-full rounded-lg border border-[var(--border)] bg-[var(--card)] px-3 text-sm focus:border-[var(--ring)] focus:outline-none"
      />
    </FieldShell>
  );
}

function zeroMeansNoLimit(key: string) {
  return normalizeConfigKey(key) === "timeout";
}

function normalizeConfigKey(key: string) {
  return key.replace(/([a-z0-9])([A-Z])/g, "$1_$2").toLowerCase();
}

function StructuredField({
  field,
  value,
  draft,
  error,
  setDraft,
}: Pick<CatalogFieldProps, "field" | "value" | "draft" | "error" | "setDraft">) {
  return (
    <FieldShell label={field.label}>
      <HighlightedCodeEditor
        aria-label={field.label}
        language={configEditorLanguage(field.key, value)}
        value={draft}
        onChange={(event) => setDraft(field.key, event.target.value)}
        rows={7}
        minHeight={160}
        invalid={!!error}
      />
      {error && <p className="mt-1 text-xs text-[var(--destructive)]">{error}</p>}
    </FieldShell>
  );
}
